import React, { useState, useEffect } from 'react';
import { CheckIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { userApi, pricingApi } from '../services/api';
import { toast } from 'react-hot-toast';

interface PricingPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  interval: string;
  features: string[];
  recommended?: boolean;
  stripePriceId: string;
}

interface CreditPackage {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  credits: number;
  stripePriceId: string;
}

export default function Subscription() {
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<PricingPlan[]>([]);
  const [creditPackages, setCreditPackages] = useState<CreditPackage[]>([]);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);

  // Check if user has active subscription (including those active until period end)
  const hasActiveSubscription = (
    userProfile?.subscriptionStatus === 'active' ||
    userProfile?.subscriptionStatus === 'active_until_period_end'
  ) && userProfile?.plan && userProfile?.plan !== 'free';


  useEffect(() => {
    loadUserData();
    loadPricingData();
  }, []);

  const loadUserData = async () => {
    try {
      setProfileLoading(true);
      const [profileResponse] = await Promise.all([
        userApi.getProfile().catch(err => {
          console.error('Failed to load profile:', err);
          return { success: false, error: 'Failed to load profile' };
        })
      ]);

      if (profileResponse.success && 'data' in profileResponse) {
        setUserProfile(profileResponse.data);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const loadPricingData = async () => {
    try {
      setPricingLoading(true);
      const response = await pricingApi.getPricing();

      if (response.success && response.data) {
        const transformedSubscriptions: PricingPlan[] = response.data.subscriptions.map(sub => ({
          id: sub.id,
          name: sub.name || 'Subscription Plan',
          description: sub.description || `${sub.credits || 'Messages'} per month`,
          price: sub.price,
          currency: sub.currency,
          interval: sub.interval,
          features: sub.features || [`${sub.credits || 'Messages'} per month`],
          recommended: sub.recommended || false,
          stripePriceId: sub.stripePriceId
        }));

        const transformedCreditPackages: CreditPackage[] = response.data.creditPackages.map(pkg => ({
          id: pkg.id,
          name: pkg.name || 'Credit Pack',
          description: pkg.description || `Add ${pkg.credits} credits`,
          price: pkg.price,
          currency: pkg.currency,
          credits: pkg.credits,
          stripePriceId: pkg.stripePriceId
        }));

        setSubscriptionPlans(transformedSubscriptions);
        setCreditPackages(transformedCreditPackages);
      } else {
        toast.error('Failed to load pricing data');
      }
    } catch (error) {
      console.error('Error loading pricing data:', error);
      toast.error('Failed to load pricing data');
    } finally {
      setPricingLoading(false);
    }
  };

  const handleSubscribe = async (priceId: string) => {
    setLoading(true);
    try {
      const response = await userApi.createCheckoutSession(priceId, 'subscription');

      if (response.success && response.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to start checkout process');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyCredits = async (priceId: string) => {
    setLoading(true);
    try {
      const response = await userApi.createCheckoutSession(priceId, 'payment');

      if (response.success && response.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to start checkout process');
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setLoading(true);
    try {
      const response = await userApi.createStripePortalSession();

      if (response.success && response.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.error(response.error || 'Failed to open billing portal');
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast.error('Failed to open billing portal');
    } finally {
      setLoading(false);
    }
  };

  if (profileLoading || pricingLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-6 h-6 border border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Subscription Plans - Only show if user doesn't have active subscription */}
      {!hasActiveSubscription && (
        <div className="mb-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Choose Your Plan</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Select a monthly subscription plan</p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {subscriptionPlans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white dark:bg-slate-800 rounded-2xl border ${
                  plan.recommended
                    ? 'border-slate-700 dark:border-white ring-1 ring-slate-700 dark:ring-white'
                    : 'border-gray-200 dark:border-slate-700'
                } p-6 hover:shadow-md transition-all`}
              >
                {plan.recommended && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 bg-slate-700 dark:bg-white text-white dark:text-slate-900 px-3 py-1 rounded-full text-xs font-medium">
                      <SparklesIcon className="w-3 h-3" />
                      Popular
                    </span>
                  </div>
                )}

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {plan.name}
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {plan.description}
                </p>

                <div className="mt-4 mb-6">
                  <span className="text-3xl font-bold text-gray-900 dark:text-white">
                    ${plan.price}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    /{plan.interval}
                  </span>
                </div>

                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckIcon className="flex-shrink-0 h-4 w-4 text-emerald-500 mt-0.5" />
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe(plan.stripePriceId)}
                  disabled={loading}
                  className={`w-full py-2.5 px-4 rounded-full text-sm font-medium transition-colors disabled:opacity-50 ${
                    plan.recommended
                      ? 'bg-slate-700 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-gray-100'
                      : 'border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  {loading ? 'Processing...' : 'Subscribe'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Credit Packages - Only show if user has active subscription */}
      {hasActiveSubscription && (
        <div className="mb-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Buy Credits</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Purchase additional credits for your account</p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {creditPackages.map((pkg) => (
              <div
                key={pkg.id}
                className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6 hover:shadow-md transition-all"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {pkg.credits.toLocaleString()} Credits
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {pkg.description}
                </p>

                <div className="mt-4 mb-6">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">
                    ${pkg.price}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400 text-sm ml-1">
                    one-time
                  </span>
                </div>

                <button
                  onClick={() => handleBuyCredits(pkg.stripePriceId)}
                  disabled={loading}
                  className="w-full py-2.5 px-4 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-full text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Buy Credits'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Billing Management */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Billing</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage payment methods and view invoices
            </p>
          </div>
          <button
            onClick={handleManageBilling}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-full text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
          >
            {loading ? 'Opening...' : 'Manage Billing'}
          </button>
        </div>
      </div>
    </div>
  );
}
